/**
 * Participant-facing wording that the application, rather than the HTML, is
 * responsible for.
 *
 * The bridge message is the reason this file exists. It is a methodological
 * instrument, not decoration: it tells a participant that answering
 * differently is as valid a result as answering the same, which is what stops
 * the post stage measuring memory instead of opinion. A test asserts it
 * character for character so that a well-meaning edit cannot quietly soften
 * it.
 */
export const BRIDGE_MESSAGE =
  'We are asking these questions again because your views may or may not have changed. ' +
  'Remaining the same, becoming more favorable, and becoming less favorable are all equally ' +
  'informative. Please answer based on how you feel now, without trying to reproduce your ' +
  'earlier answers.';

/** Headings and body text for each screen a participant can land on. */
export const SCREEN_COPY = {
  'pre-not-yet': {
    heading: 'Not open yet',
    body: 'The questions for this event have not opened. Leave this page open, or come back to the same link on this device — it will update on its own.',
  },
  'pre-form': {
    heading: 'Before we begin',
    body: 'Please say how much you agree with each statement. All three are required. Once you submit, your answers are sealed: you will not be able to see or change them.',
  },
  holding: {
    heading: 'Answers received',
    body: 'Your answers are saved and sealed. We will ask the same questions again after the event. You can close this page — just come back to the same link on this device.',
  },
  'post-form': {
    heading: 'After the event',
    body: BRIDGE_MESSAGE,
  },
  done: {
    heading: 'All done',
    body: 'Thank you. Both sets of answers are recorded.',
  },
  'pre-missed': {
    heading: 'Sorry — too late for this one',
    body: 'The questions asked before the event have closed. The questions asked afterwards only go to people who answered beforehand, so there is nothing to compare against. Thank you for scanning.',
  },
  'post-closed': {
    heading: 'Closed',
    body: 'The questions asked after the event have closed. Thank you for taking part.',
  },
} as const;

/**
 * Messages shown when the browser cannot reach the server or the server
 * refuses.
 *
 * The first three are the browser's own; the rest are the sentences the Worker
 * sends back, which it takes from here so that the two halves cannot drift
 * into two ways of saying the same thing.
 */
export const ERROR_COPY = {
  offline: 'Cannot reach the server. Check your connection — this page keeps trying.',
  submitFailed: 'Your answers were not saved. Nothing has been lost; please try again.',
  unanswered: 'Please answer every question before submitting.',
  stageClosed: 'That stage closed a moment ago, so nothing was saved.',
  noPre: 'These questions only go to people who answered before the event.',
  configChanged: 'The questions changed while you were answering. Please reload the page.',
  conflict: 'Something changed while you were answering. Please try again.',
  emailRequired: 'Please enter your email address.',
  emailInvalid: 'That does not look like an email address.',
  handoffNotYet: 'The discussion on Sway opens once the event ends.',
  handoffOff: 'This event is not set up to create Sway accounts.',
  swayUnavailable: 'Sway could not be reached just now. Please try again in a moment.',
} as const;

/** The one-line note about what is stored, shown under both forms. */
export const PRIVACY_NOTE =
  'No name, email address, or other identifying detail is collected. This device holds a random ID so your two sets of answers can be paired.';

/** The same note when the organizer has asked for an email address. */
export const PRIVACY_NOTE_WITH_EMAIL =
  'Your email address is used only to set up your Sway account. Your answers are stored separately, under a random ID this device holds so your two sets can be paired.';

/** The email field, wherever it is asked. */
export const EMAIL_COPY = {
  label: 'Email',
  placeholder: 'School email (if you have one)',
  hintPre:
    'Used once, after the event, to set up your Sway account so you can join the discussion.',
  hintPost: 'Used once, right now, to set up your Sway account so you can join the discussion.',
  missing: 'Please enter your email address.',
  invalid: 'That does not look like an email address.',
  /**
   * Shown when Submit is pressed with the field empty. The answers are the
   * survey's and nobody is kept from submitting them; but joining the
   * discussion is the point of the evening, so leaving without an address is
   * a second, deliberate press and never the default.
   */
  nag: 'Without an email address we cannot set up your Sway account, and you would miss the discussion.',
  nagAdd: 'Add my email address',
  nagSkip: 'Submit without joining Sway',
} as const;

/** The invitation to Sway, on the screens whose only way forward is the discussion. */
export const SWAY_INVITATION = {
  heading: 'Continue the discussion with a peer',
  body: "You're invited to disagree better. Sway is text-based, private, and you can use a pseudonym. Chat when convenient over the next few days. It takes guts to engage openly with someone who thinks differently from you, so we encourage you to give it a try.",
} as const;

/**
 * What the screens that turn people away from the questions say once the
 * discussion is open to them instead. The questions are paired and a
 * latecomer cannot be; the discussion is for everybody who was in the room.
 */
export const SWAY_SCREEN_COPY = {
  'pre-missed': SWAY_INVITATION,
  'post-closed': {
    heading: 'Closed',
    body: 'The questions asked after the event have closed. Thank you for taking part. The discussion on Sway is still open.',
  },
  'post-skip': {
    heading: 'Straight to the discussion',
    body: 'You can join the discussion on Sway without answering the questions. If you would rather answer first, go back — they take a minute.',
  },
  /** Added to the too-late screen while the discussion has not opened yet. */
  waiting:
    'The discussion on Sway opens once the event ends. Leave this page open and it will offer to sign you in.',
} as const;

/** The step between the last answer and Sway. */
export const HANDOFF_COPY = {
  working: 'Setting up your Sway account…',
  ready: 'Your Sway account is ready. Taking you there…',
  continue: 'Continue to Sway',
  retry: 'Try again',
  fallback: 'Or register on Sway yourself',
  failed:
    'Sway could not be reached just now. Your answers are safe; please try again in a moment.',
  refused: 'Sway would not create an account for that address.',
  emailNeeded: 'Enter your email address to set up your Sway account.',
  emailHint: 'Used once, to set up your Sway account so you can join the discussion.',
  skip: 'Go to Sway without answering',
  back: 'Back to the questions',
} as const;
